import React, { useContext, useState } from 'react';
import { Grid, IconButton } from '@material-ui/core';
import { css } from '@emotion/react';
import { compose } from 'recompose';
import withFeatureFlag from '@/components/Auth/withFeatureFlag';
import { SAAS_V1 } from '@/constants/featureFlags';

import { colors } from '@/styles';
import { Tabs, TabsList, TabsTrigger } from '@onward-delivery/design';
import { Info as InfoIcon } from '@onward-delivery/design/icons';

import { Context, ContextProvider } from './store';
import { PRIMARY_TABS } from './constants';
import { PageTitle } from './blocks';

import EditResourceModal from './modals/EditResourceModal';
import StatusGuideModal from './modals/StatusGuideModal';
import { CALENDAR_VIEWS } from '@onward-delivery/design/calendar';

import Calendar from './Calendar';
import Fleet from './Fleet';
import DaySideDialog from './DaySideDialog';
import Snackbar from '../Snackbar';

const Resources = () => {
    const { callbacks, state, loading } = useContext(Context);
    // Reference content with no data behind it, so it stays out of the store.
    const [guideOpen, setGuideOpen] = useState(false);

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                flex-wrap: nowrap;
            `}
        >
            {/* Title and tabs share one surface, as on the Inventory dashboard: the tabs belong to
                the page header rather than floating above the content they switch. */}
            <Grid
                container
                item
                direction="column"
                css={css`
                    padding: 32px 44px 0;
                    background-color: ${colors.white.primary};
                    flex: 0;
                    flex-basis: 0;
                    flex-wrap: nowrap;
                `}
            >
                <Grid
                    container
                    direction="row"
                    css={css`
                        align-items: center;
                        justify-content: space-between;
                        flex-wrap: nowrap;
                    `}
                >
                    <PageTitle>Resources</PageTitle>
                    <Grid
                        container
                        direction="row"
                        css={css`
                            flex: 0;
                            flex-basis: 0;
                            align-items: center;
                            flex-wrap: nowrap;
                            gap: 12px;
                        `}
                    >
                        {/* Native title rather than the design Tooltip: this sits outside the
                            Calendar's TooltipProvider, and one hint does not justify a second provider. */}
                        <IconButton
                            aria-label="What the badges and statuses mean"
                            title="What the badges and statuses mean"
                            onClick={() => setGuideOpen(true)}
                            css={css`
                                color: #4c4c4c;

                                svg {
                                    width: 20px;
                                    height: 20px;
                                }
                            `}
                        >
                            <InfoIcon />
                        </IconButton>
                    </Grid>
                </Grid>

                {/* Bare wrapper: `.onward-design .mt-4` matches descendants only, so the layout
                    class goes on the child rather than beside the scope. */}
                <div className="onward-design">
                    <div className="mt-4">
                        <Tabs value={state.primaryTab} onValueChange={callbacks.setPrimaryTab}>
                            <TabsList variant="underline">
                                <TabsTrigger value={PRIMARY_TABS.CALENDAR}>Calendar</TabsTrigger>
                                <TabsTrigger value={PRIMARY_TABS.FLEET}>Fleet</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>
                </div>
            </Grid>

            <Grid
                container
                direction="row"
                css={css`
                    padding: 16px 44px 0;
                    flex: 1;
                    flex-basis: 0;
                    min-height: 0;
                    /* Both dimensions: without min-width a flex item sizes to its content, so the
                       fleet table widened the page instead of scrolling inside it. */
                    min-width: 0;
                `}
            >
                {state.primaryTab === PRIMARY_TABS.FLEET ? (
                    <Fleet
                        resources={state.resources}
                        subregionsOrdered={state.subregionsOrdered}
                        today={state.today}
                        onEdit={callbacks.editResource}
                    />
                ) : (
                    <Calendar />
                )}
            </Grid>

            {/* The card sections only describe cells that render cards, which is the week layout. */}
            <StatusGuideModal
                open={guideOpen}
                onClose={() => setGuideOpen(false)}
                showResourceCards={state.calendarView === CALENDAR_VIEWS.WEEK}
            />

            <DaySideDialog />

            <EditResourceModal
                {...state.editing}
                isLoading={loading.editModal}
                subregions={state.subregionsById}
                subregionsOrdered={state.subregionsOrdered}
                callbacks={{
                    saveResource: callbacks.upsertResources,
                    onClose: callbacks.clearEdit,
                    deleteResource: callbacks.deleteResource,
                }}
            />

            <Snackbar
                open={!!state.notification?.message}
                {...state.notification}
                handleClose={callbacks.clearNotification}
            />
        </Grid>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withFeatureFlag(SAAS_V1), withContext)(Resources);
